<template>
  <div
    :class="[
      'vc-popover',
      {
        'vc-popover--arrow': arrowEnabled && arrowLeft,
      },
    ]"
  >
    <div v-if="disableTriggerEvents" ref="reference" class="vc-popover__trigger">
      <slot name="trigger" :open="openPopover" :close="closePopover" :toggle="togglePopover" :opened="opened" />
    </div>

    <div
      v-else
      ref="reference"
      class="vc-popover__trigger"
      role="button"
      tabindex="0"
      @mouseenter="hover && openPopover()"
      @mouseleave="hover && closePopover()"
      @focusin="hover && openPopover()"
      @focusout="hover && closePopover()"
      @blur="hover && closePopover()"
      @click="!hover && togglePopover()"
      @keyup.enter="!hover && openPopover()"
      @keyup.esc="!hover && closePopover()"
    >
      <slot name="trigger" :open="openPopover" :close="closePopover" :toggle="togglePopover" :opened="opened" />
    </div>
    <!-- !! IMPORTANT !! -  KEEP ID on the element below -->
    <div
      :id="`popover-${$.uid}`"
      ref="floating"
      :style="{ zIndex, display, width, ...floatingStyles }"
      :class="[
        'vc-popover__content',
        {
          'position-bottom': opened && isPlacementBottom,
          'position-top': opened && !isPlacementBottom,
        },
      ]"
      :role="role"
    >
      <div
        v-if="arrowEnabled"
        ref="floatingArrow"
        class="vc-popover__popper"
        :style="{
          left: arrowLeft,
        }"
      >
        <div class="vc-popover__arrow"></div>
      </div>

      <slot name="content" :close="closePopover" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { flip, offset, shift, useFloating, autoUpdate, arrow } from "@floating-ui/vue";
import { onClickOutside } from "@vueuse/core";
import { ref, toRefs, computed, watch } from "vue";

interface IEmits {
  (event: "toggle", value: boolean): void;
  (event: "update:open", value: boolean): void;
}

interface IProps {
  placement?: VcPopoverPlacementType;
  strategy?: VcPopoverStrategyType;
  flipOptions?: VcPopoverFlipOptionsType;
  offsetOptions?: VcPopoverOffsetOptionsType;
  shiftOptions?: VcPopoverShiftOptionsType;
  disabled?: boolean;
  width?: string;
  zIndex?: number | string;
  role?: string;
  hover?: boolean;
  disableTriggerEvents?: boolean;
  arrowEnabled?: boolean;
  open?: boolean;
}

const emit = defineEmits<IEmits>();

const props = withDefaults(defineProps<IProps>(), {
  zIndex: 10,
  placement: "bottom",
  width: "auto",
  open: undefined,
});

// Initialize with a local variable, then set the ref
const initialOpenState = props.open ?? false;
const opened = ref(initialOpenState);

const reference = ref<HTMLElement | null>(null);
const floating = ref<HTMLElement | null>(null);
const floatingArrow = ref<Element | null>(null);
const { placement, strategy, flipOptions, offsetOptions, shiftOptions } = toRefs(props);

const display = computed(() => (opened.value ? "block" : ""));
const arrowLeft = computed(() => (middlewareData.value.arrow?.x != null ? `${middlewareData.value.arrow.x}px` : ""));

const {
  floatingStyles,
  middlewareData,
  placement: resolvedPlacement,
} = useFloating(reference, floating, {
  placement,
  strategy,
  transform: false,
  middleware: [
    flip(flipOptions.value),
    offset(offsetOptions.value),
    shift(shiftOptions.value),
    arrow({ element: floatingArrow }),
  ],
  whileElementsMounted(...args) {
    return autoUpdate(...args, { animationFrame: true });
  },
});

const isPlacementBottom = computed(() => resolvedPlacement.value.startsWith("bottom"));

function openPopover() {
  if (props.disabled) {
    return;
  }

  opened.value = true;
}

function closePopover() {
  if (props.disabled) {
    return;
  }

  opened.value = false;
}

function togglePopover() {
  if (props.disabled) {
    return;
  }

  opened.value = !opened.value;
}

onClickOutside(
  reference,
  () => {
    closePopover();
  },
  { ignore: [floating] },
);

// Watch for changes to opened state and emit events
watch(opened, (value: boolean) => {
  emit("toggle", value);
  emit("update:open", value);
});

// Watch for changes to the open prop
watch(
  () => props.open,
  (value) => {
    if (value !== undefined && value !== opened.value) {
      opened.value = value;
    }
  },
  { immediate: true },
);
</script>

<style lang="scss">
.vc-popover {
  $arrow: "";

  &--arrow {
    $arrow: &;
  }

  &__popper {
    @apply absolute top-0 w-5 h-2.5 p-1 overflow-hidden;
  }

  &__arrow {
    @apply w-3 h-3 bg-additional-50 rotate-45 shadow-md;
  }

  &__content {
    @apply hidden absolute top-0 left-0;

    #{$arrow} & {
      @apply pt-2.5;
    }
  }
}
</style>
